import React, { useState } from 'react'
import styled, { css } from 'styled-components'
import { FiCheck, FiEye, FiEyeOff } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'

const InputWrapper = styled.div<{ hasError: boolean, isInputValid: boolean }>`
  display: flex;
  align-items: center;
  background-color: #f2f4f6;
  border-radius: ${props => props.theme.spacing.small};
  border: 1px solid;
  border-color: ${props => props.hasError ? 'red' : (props.isInputValid ? props.theme.colors.primary : props.theme.colors.lightGray)};
  width: 100%;

  ${props => props.hasError && css`
    &&:focus-within {
      border-color: red;
      box-shadow: 0 0 0 3px rgba(255, 0, 0, 0.14);
    }
  `}
`

const IconWrapper = styled.div`
  margin-right: ${props => props.theme.spacing.small};
  display: flex;
  align-items: center;
  color: ${props => props.theme.colors.lightGray};
`

/** Exported so screens can address the toggle itself instead of matching on markup. */
export const PasswordToggle = styled.div`
  display: flex;
  align-items: center;
  margin-right: ${props => props.theme.spacing.small};
  color: ${props => props.theme.colors.lightGray};
  cursor: pointer;
`

const CustomInput = styled.input`
  width: 100%;
  background-color: transparent;
  border: none;
  outline: none;
  margin-left: 4px;
  align-items: center;
  font-size: ${props => props.theme.fontSizes.medium};
  font-family: Fira Sans,sans-serif;

  ::placeholder {
    color: #999;
  }
`

const Padding = styled.div`
  padding: 12px;
  display: flex;
  align-items: center;
  width: 100%;
`

export type AutoCompleteType =
  | 'off'
  | 'on'
  | 'given-name'
  | 'email'
  | 'username'
  | 'current-password'
  | 'new-password'

interface IconInputProps {
  icon?: React.ReactNode
  placeholder?: string
  type?: React.InputHTMLAttributes<HTMLInputElement>['type']
  inputMode?: React.InputHTMLAttributes<HTMLInputElement>['inputMode']
  name: string
  value?: string
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  autoComplete?: AutoCompleteType
  autoCapitalize?: boolean
  onBlur: (event: React.FocusEvent<HTMLInputElement>) => void
  onFocus: (event: React.FocusEvent<HTMLInputElement>) => void
  hasError: boolean
  isInputValid: boolean
  maxLength?: number
}

const IconInput: React.FC<IconInputProps> = ({
  icon,
  placeholder,
  type = 'text',
  inputMode,
  name,
  value,
  onChange,
  autoComplete = 'on',
  autoCapitalize = false,
  onBlur,
  onFocus,
  hasError,
  isInputValid,
  maxLength
}) => {
  const { t } = useTranslation()

  const [inputType, setInputType] = useState<React.InputHTMLAttributes<HTMLInputElement>['type']>(type)
  const isPassword = type === 'password'

  return (
    <InputWrapper hasError={hasError} isInputValid={isInputValid}>
      <Padding>
        {icon && <IconWrapper>{icon}</IconWrapper>}
        <CustomInput
          type={inputType}
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          autoCapitalize={autoCapitalize ? 'auto' : 'none'}
          onBlur={onBlur}
          onFocus={onFocus}
          autoComplete={autoComplete}
          maxLength={maxLength}
          inputMode={inputMode}
        />
        {isPassword && (
          <>
            {inputType === 'password'
              ? (
              <PasswordToggle
                role="button"
                aria-label={`${t('forms.showPassword')}`}
                data-testid="password-toggle"
                onClick={() => setInputType('text')}>
                <FiEyeOff />
              </PasswordToggle>
                )
              : (
              <PasswordToggle
                role="button"
                aria-label={`${t('forms.hidePassword')}`}
                data-testid="password-toggle"
                onClick={() => setInputType('password')}>
                <FiEye />
              </PasswordToggle>
                )}
          </>
        )}
        {isInputValid && <FiCheck aria-label={`${t('forms.inputIsValid')}`} role="img" />}
      </Padding>
    </InputWrapper>
  )
}

export default IconInput
